import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { get } from "@insite/client-framework/Services/ApiService";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import { PumpReturnEmailRequest } from "@preferredMedical/Models/CustomForms/PumpReturn/PumpReturnEmailRequest";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { sendPumpReturnEmail } from "@preferredMedical/Services/CustomFormsService";
import { emailField, requiredStringField } from "@preferredMedical/Services/FormValidation";
import { generateSelectOptions } from "@preferredMedical/Services/SelectHelper";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    emailRecipients: string;
    productNames: string;
}

type Props = OwnProps;

export interface PumpReturnStyles {
    container?: GridContainerProps;
    containerResponsive?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    threeLinerGridItem?: GridItemProps;
    fourLinerGridItem?: GridItemProps;
    sixLinerGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    submitButtonWrapper?: InjectableCss;
    addButton?: ButtonPresentationProps;
    inlineCheckbox?: InjectableCss;
    columnView?: InjectableCss;
    formSecondaryTitle?: InjectableCss;
    verticalSpacing?: InjectableCss;
    addButtonWrapper?: InjectableCss;
    mainWrapper?: GridContainerProps;
}

const pumpReturnTypes = [
    { key: uniqueId(), value: "Preventive Maintenance" },
    { key: uniqueId(), value: "Rental Return" },
    {
        key: uniqueId(),
        value: "Repair",
    },
] as const;

export interface PumpInformation {
    pumpName: string;
    pumpActualName: string;
    pumpSerialNumber: string;
    pumpReturnType: string;
    needReplacementRental: string;
    pumpComments: string;
    rentCurrentPump: string;
    key: string;
}

export interface PumpReturnFormData {
    contactName: string;
    companyName: string;
    customerNumber: string;
    returnAddressStreet: string;
    city: string;
    phone: string;
    state: string;
    zipCode: string;
    email: string;
    poNumber: string;
    pumpInformation: PumpInformation[];
    noPORequired: boolean;

    contactNumberOnChange: EventEx;
    contactNumberUpdate: EventEx;
}

export interface PumpReturnFormErrors {
    contactName: string;
    companyName: string;
    customerNumber: string;
    returnAddressStreet: string;
    city: string;
    state: string;
    phone: string;
    zipCode: string;
    email: string;
    poNumber: string;
    firstPumpName: string;
    firstPumpSerialNumber: string;
    firstPumpReturnType: string;
    firstPumpComments: string;
}

const initialFormErrors: PumpReturnFormErrors = {
    contactName: "",
    companyName: "",
    customerNumber: "",
    returnAddressStreet: "",
    city: "",
    state: "",
    phone: "",
    zipCode: "",
    email: "",
    poNumber: "",
    firstPumpName: "",
    firstPumpSerialNumber: "",
    firstPumpReturnType: "",
    firstPumpComments: "",
};

const initialFormData: PumpReturnFormData = {
    contactName: "",
    companyName: "",
    customerNumber: "",
    returnAddressStreet: "",
    city: "",
    state: "",
    phone: "",
    zipCode: "",
    email: "",
    poNumber: "",
    pumpInformation: [
        {
            pumpName: "",
            pumpActualName: "",
            pumpSerialNumber: "",
            pumpReturnType: "",
            needReplacementRental: "Yes",
            pumpComments: "",
            rentCurrentPump: "Yes",
            key: uniqueId(),
        },
    ],
    noPORequired: false,

    contactNumberOnChange: new EventEx(),
    contactNumberUpdate: new EventEx(),
};

const maxNumberOfReturnPumps = 15;

export const styles: PumpReturnStyles = {
    container: {
        gap: 10,
        css: css`
            text-align: left;
            margin-bottom: 20px;
        `,
    },
    containerResponsive: {
        gap: 10,
        css: css`
            text-align: left;
            margin-bottom: 20px;
            padding-left: 5px;
            padding-right: 5px;
            @media (max-width: 991px) {
                div[class*="GridItemStyle"] {
                    max-width: 50%;
                    flex-basis: 50%;
                }
            }
        `,
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding: 5px;
        `,
    },
    threeLinerGridItem: {
        width: 4,
        css: css`
            padding: 5px;
        `,
    },
    fourLinerGridItem: {
        width: 3,
        css: css`
            padding: 5px;
        `,
    },
    sixLinerGridItem: {
        width: 2,
        css: css`
            padding: 5px;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
        `,
    },
    addButton: {
        variant: "secondary",
        css: css`
            text-align: center;
        `,
    },
    inlineCheckbox: {
        css: css`
            display: flex;
            flex-direction: column;
            div[class*="Checkbox"] {
                margin-left: 10px;

                span {
                    + label {
                        margin-left: 5px;
                    }
                }

                + div[class*="Checkbox"] {
                    margin-top: 0;
                }
            }
        `,
    },
    columnView: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    formSecondaryTitle: {
        css: css`
            border-top: 1px solid #cccccc;
            padding-top: 20px;
            margin-top: 30px;
            margin-bottom: 30px;
            width: 100%;
            text-align: center;
            font-size: 20px;
            font-weight: 700;
        `,
    },
    verticalSpacing: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
        `,
    },
    mainWrapper: {
        css: css`
            width: 100%;
            margin: 0;
            padding-left: 15px;
            padding-right: 15px;
        `,
    },
    addButtonWrapper: {
        css: css`
            border-bottom: 1px solid #ccc;
            margin-bottom: 30px;
            padding-bottom: 30px;
            width: 100%;
            text-align: center;
        `,
    },
    submitButtonWrapper: {
        css: css`
            min-width: 320px;
        `,
    },
};

const getRentableProductNames = async () => {
    const rentableProductNames = await get<ProductModel[]>("api/v1/Product/RentableProducts");

    return rentableProductNames.map(x => x.productTitle);
};

export interface PumpReturnFormState {
    inProgress: boolean;
    formData: PumpReturnFormData;
    formErrors: PumpReturnFormErrors;
    rentableProductNames: string[];
}

const requiredFieldMessage = translate("This is a required field.");
const emailFieldMessage = translate("Enter a valid email address.");

class PumpReturnForm extends React.Component<Props, PumpReturnFormState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            inProgress: false,
            formData: { ...initialFormData },
            formErrors: { ...initialFormErrors },
            rentableProductNames: [...this.props.productNames.split("\n")],
        };

        this.state.formData.contactNumberOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await this.setState({ ...this.state, formData: { ...this.state.formData, phone: phoneNumberState.value } });
            await this.setState({
                ...this.state,
                formErrors: { ...this.state.formErrors, phone: phoneNumberState.error },
            });
        });

        this.checkForErrors = this.checkForErrors.bind(this);
        this.submitButtonClickHandler = this.submitButtonClickHandler.bind(this);
        this.addRow = this.addRow.bind(this);
        this.updateFreeTextPumpRow = this.updateFreeTextPumpRow.bind(this);
        this.updatePumpRowField = this.updatePumpRowField.bind(this);
        this.updatePumpRowReturnType = this.updatePumpRowReturnType.bind(this);
    }

    componentDidMount() {
        getRentableProductNames().then(value => {
            this.setState({ ...this.state, rentableProductNames: [...value, ...this.props.productNames.split("\n")] });
        });
    }

    checkForErrors = async () => {
        let hasError = false;
        const currentFormErrors: PumpReturnFormErrors = { ...initialFormErrors };
        const formData = this.state.formData;

        ({ hasError, errorMessage: currentFormErrors.contactName } = requiredStringField(
            formData.contactName,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.companyName } = requiredStringField(
            formData.companyName,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.customerNumber } = requiredStringField(
            formData.customerNumber,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.returnAddressStreet } = requiredStringField(
            formData.returnAddressStreet,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.city } = requiredStringField(
            formData.city,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.state } = requiredStringField(
            formData.state,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.zipCode } = requiredStringField(
            formData.zipCode,
            requiredFieldMessage,
            hasError,
        ));
        if (!formData.noPORequired) {
            ({ hasError, errorMessage: currentFormErrors.poNumber } = requiredStringField(
                formData.poNumber,
                requiredFieldMessage,
                hasError,
            ));
        }
        ({ hasError, errorMessage: currentFormErrors.email } = emailField(formData.email, emailFieldMessage, hasError));

        await this.state.formData.contactNumberUpdate.emitAsync();
        if (this.state.formErrors.phone) {
            hasError = true;
        }

        if (formData.pumpInformation && formData.pumpInformation.length > 0) {
            ({ hasError, errorMessage: currentFormErrors.firstPumpName } = requiredStringField(
                formData.pumpInformation[0]?.pumpName,
                requiredFieldMessage,
                hasError,
            ));
            ({ hasError, errorMessage: currentFormErrors.firstPumpSerialNumber } = requiredStringField(
                formData.pumpInformation[0]?.pumpSerialNumber,
                requiredFieldMessage,
                hasError,
            ));
            ({ hasError, errorMessage: currentFormErrors.firstPumpComments } = requiredStringField(
                formData.pumpInformation[0]?.pumpComments,
                requiredFieldMessage,
                hasError,
            ));

            if (!formData.pumpInformation[0]?.pumpReturnType) {
                hasError = true;
                currentFormErrors.firstPumpReturnType = requiredFieldMessage;
            }
        }

        await this.setState({ ...this.state, formErrors: currentFormErrors });
        return hasError;
    };

    submitButtonClickHandler = async () => {
        const hasErrors = await this.checkForErrors();
        if (hasErrors) {
            return;
        }

        this.setState({ inProgress: true });

        const pumpInformation = this.state.formData.pumpInformation
            .filter(pi => {
                return (pi.pumpName && pi.pumpName.trim() !== "")
                    || (pi.pumpSerialNumber && pi.pumpSerialNumber.trim() !== "");
            })
            .map(pumpInfo => ({
                pumpName:
                    pumpInfo.pumpName === "Other" ? pumpInfo.pumpActualName : pumpInfo.pumpName,
                pumpSerialNumber: pumpInfo.pumpSerialNumber,
                pumpReturnType: pumpInfo.pumpReturnType,
                needReplacementRental: pumpInfo.needReplacementRental,
                pumpComments: pumpInfo.pumpComments,
                rentCurrentPump: pumpInfo.rentCurrentPump,
            }));

        const emailTos = this.props.emailRecipients
            .split("\n")

            .map(addr => addr.trim())
            .filter(addr => addr !== "");

        const model: PumpReturnEmailRequest = {
            contactName: this.state.formData.contactName,
            companyName: this.state.formData.companyName,
            customerNumber: this.state.formData.customerNumber,
            returnAddressStreet: this.state.formData.returnAddressStreet,
            city: this.state.formData.city,
            state: this.state.formData.state,
            zipCode: this.state.formData.zipCode,
            phone: this.state.formData.phone,
            email: this.state.formData.email,
            poNumber: this.state.formData.poNumber,
            pumpInformation,
            emailTos,
        };
        
        await sendPumpReturnEmail(model);

        const confirmationPageUrl = `services/PumpReturnForm/PumpReturnConfirmation`;
        if (confirmationPageUrl) {
            window.location.href = confirmationPageUrl;
        }
    };

    addRow = () => {
        if (this.state.formData.pumpInformation.length >= maxNumberOfReturnPumps) {
            return;
        }

        const newPumpRow = {
            pumpName: "",
            pumpActualName: "",
            pumpSerialNumber: "",
            pumpReturnType: "",
            needReplacementRental: "Yes",
            pumpComments: "",
            rentCurrentPump: "Yes",
            key: uniqueId(),
        };

        this.setState({
            ...this.state,
            formData: { ...this.state.formData, pumpInformation: [...this.state.formData.pumpInformation, newPumpRow] },
        });
    };

    updateFreeTextPumpRow = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
        index: number,
        name?: string,
    ) => {
        const value = e.target.value;
        const fieldNameToUpdate = name ?? e.target.name;
        this.updatePumpRowField(value, index, fieldNameToUpdate);
    };

    updatePumpRowField = (value: string, index: number, fieldNameToUpdate: string) => {
        const pumpInformationCopy = [...this.state.formData.pumpInformation];
        let pumpInformationRowCopy = pumpInformationCopy[index];

        pumpInformationRowCopy = {
            ...pumpInformationRowCopy,
            [fieldNameToUpdate]: value,
        };

        pumpInformationCopy[index] = pumpInformationRowCopy;
        this.setState({ ...this.state, formData: { ...this.state.formData, pumpInformation: pumpInformationCopy } });
    };

    updatePumpRowReturnType = (pumpReturnType: string, index: number) => {
        const pumpInformationCopy = [...this.state.formData.pumpInformation];
        pumpInformationCopy[index] = {
            ...pumpInformationCopy[index],
            pumpReturnType,
        };
        this.setState({ formData: { ...this.state.formData, pumpInformation: pumpInformationCopy } });
    };

    render() {
        const productOptions = generateSelectOptions(this.state.rentableProductNames, true, "Select A Pump");
        const booleanDropdownOptions = generateSelectOptions(["Yes", "No"], false);

        return (
            <>
                <StyledWrapper {...styles.mainWrapper}>
                    <GridContainer {...styles.container} data-test-selector="pumpReturnForm">
                        <GridItem {...styles.oneLinerGridItem}>
                            <TextField
                                label={translate("Contact Name")}
                                value={this.state.formData.contactName}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, contactName: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnContactName"
                                error={this.state.formErrors.contactName}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("Company Name")}
                                value={this.state.formData.companyName}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, companyName: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnCompanyName"
                                error={this.state.formErrors.companyName}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("Customer Number")}
                                value={this.state.formData.customerNumber}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, customerNumber: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnCustomerNumber"
                                error={this.state.formErrors.customerNumber}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("Return Street Address")}
                                value={this.state.formData.returnAddressStreet}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, returnAddressStreet: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnReturnAddressStreet"
                                error={this.state.formErrors.returnAddressStreet}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("City")}
                                value={this.state.formData.city}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, city: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnCity"
                                error={this.state.formErrors.city}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("State")}
                                value={this.state.formData.state}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, state: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnState"
                                error={this.state.formErrors.state}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("Zip Code")}
                                value={this.state.formData.zipCode}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, zipCode: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnZipCode"
                                error={this.state.formErrors.zipCode}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <PhoneNumberField
                                label={translate("Phone")}
                                initialValue={this.state.formData.phone}
                                onChange={this.state.formData.contactNumberOnChange}
                                update={this.state.formData.contactNumberUpdate}
                                data-test-selector="pumpRentalContactNumber"
                                format="000-000-0000"
                                required={true}
                                hideRequiredAsterisk={false}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                label={translate("Email")}
                                value={this.state.formData.email}
                                onChange={e =>
                                    this.setState({
                                        ...this.state,
                                        formData: { ...this.state.formData, email: e.target.value },
                                    })
                                }
                                data-test-selector="pumpReturnEmail"
                                error={this.state.formErrors.email}
                                required={true}
                            />
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <TextField
                                    label={translate("PO# (For Your Records)")}
                                    value={this.state.formData.poNumber}
                                    onChange={e =>
                                        this.setState({
                                            ...this.state,
                                            formData: { ...this.state.formData, poNumber: e.target.value },
                                        })
                                    }
                                    data-test-selector="pumpReturnPONumber"
                                    error={this.state.formErrors.poNumber}
                                    required={!this.state.formData.noPORequired}
                                />
                                <div style={{ marginTop: '10px' }}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={this.state.formData.noPORequired}
                                            onChange={async e => {
                                                await this.setState({
                                                    ...this.state,
                                                    formData: { ...this.state.formData, noPORequired: e.target.checked },
                                                });
                                                if (e.target.checked) {
                                                    await this.setState({
                                                        ...this.state,
                                                        formErrors: { ...this.state.formErrors, poNumber: "" },
                                                    });
                                                }
                                            }}
                                        />
                                        {translate("No PO Required for Invoicing")}
                                    </label>
                                </div>
                            </div>
                        </GridItem>

                        <GridItem {...styles.oneLinerGridItem}>
                            <Typography {...styles.formSecondaryTitle}>
                                {translate("Your Pump Return Information")}
                            </Typography>
                        </GridItem>
                        {this.state.formData.pumpInformation && this.state.formData.pumpInformation.length > 0 && (
                            <GridContainer
                                {...styles.containerResponsive}
                                key={this.state.formData.pumpInformation[0].key}
                            >
                                <GridItem {...styles.fourLinerGridItem}>
                                    <StyledWrapper {...styles.columnView}>
                                        <Select
                                            label={translate(`Pump 1 Name`)}
                                            required={true}
                                            error={this.state.formErrors.firstPumpName}
                                            onChange={e => this.updatePumpRowField(e.target.value, 0, "pumpName")}
                                            value={this.state.formData.pumpInformation[0].pumpName}
                                        >
                                            {productOptions}
                                        </Select>
                                        {this.state.formData.pumpInformation[0].pumpName === "Other" && (
                                            <TextField
                                                name="pumpActualName"
                                                label={translate(`Pump 1 Name`)}
                                                value={this.state.formData.pumpInformation[0].pumpActualName}
                                                onChange={e => this.updateFreeTextPumpRow(e, 0)}
                                                required={true}
                                                error={this.state.formErrors.firstPumpName}
                                            />
                                        )}
                                        <TextField
                                            name="pumpSerialNumber"
                                            label={translate(`Pump 1 Serial Number`)}
                                            value={this.state.formData.pumpInformation[0].pumpSerialNumber}
                                            onChange={e => this.updateFreeTextPumpRow(e, 0)}
                                            required={true}
                                            error={this.state.formErrors.firstPumpSerialNumber}
                                        />
                                    </StyledWrapper>
                                </GridItem>

                                <GridItem {...styles.fourLinerGridItem}>
                                    <RadioGroup
                                        name="pumpSerialReturnType"
                                        label={translate(`Pump 1 Return Type`)}
                                        required={true}
                                        error={this.state.formErrors.firstPumpReturnType}
                                        value={this.state.formData.pumpInformation[0].pumpReturnType}
                                        onChange={(e: React.FormEvent<HTMLFieldSetElement>) => {
                                            const target = e.currentTarget as HTMLFieldSetElement;
                                            const selectedValue = (target.querySelector("input:checked") as HTMLInputElement)?.value;
                                            if (selectedValue) {
                                                this.updatePumpRowReturnType(selectedValue, 0);
                                            }
                                        }}
                                    >
                                        <StyledWrapper {...styles.inlineCheckbox}>
                                            {pumpReturnTypes.map(pumpReturnType => (
                                                <Radio
                                                    value={pumpReturnType.value}
                                                    key={pumpReturnType.key}
                                                >
                                                    {translate(pumpReturnType.value)}
                                                </Radio>
                                            ))}
                                        </StyledWrapper>
                                    </RadioGroup>
                                </GridItem>

                                <GridItem {...styles.fourLinerGridItem}>
                                    <StyledWrapper {...styles.columnView}>
                                        <Select
                                            label={translate(`Pump 1 - Need a Replacement Rental?`)}
                                            required={true}
                                            onChange={e =>
                                                this.updatePumpRowField(e.target.value, 0, "needReplacementRental")
                                            }
                                            value={this.state.formData.pumpInformation[0].needReplacementRental}
                                        >
                                            {booleanDropdownOptions}
                                        </Select>
                                        <StyledWrapper {...styles.verticalSpacing}>
                                            <Select
                                                label={translate(`Pump 1 - Do you rent the current pump from Preferred Medical?`)}
                                                required={true}
                                                onChange={e => this.updatePumpRowField(e.target.value, 0, "rentCurrentPump")}
                                                value={this.state.formData.pumpInformation[0].rentCurrentPump}
                                            >
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>
                                            </Select>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>

                                <GridItem {...styles.fourLinerGridItem}>
                                    <TextArea
                                        name="pumpComments"
                                        label={translate(`Pump 1 Comments`)}
                                        value={this.state.formData.pumpInformation[0].pumpComments}
                                        onChange={e => this.updateFreeTextPumpRow(e, 0)}
                                        required={true}
                                        error={this.state.formErrors.firstPumpComments}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                        {this.state.formData.pumpInformation.slice(1).map((pumpRow, index) => (
                            <GridContainer {...styles.containerResponsive} key={pumpRow.key}>
                                <GridItem {...styles.fourLinerGridItem}>
                                    <StyledWrapper {...styles.columnView}>
                                        <Select
                                            label={translate(`Pump ${index + 2} Name`)}
                                            placeholder="Select A Pump"
                                            onChange={e =>
                                                this.updatePumpRowField(e.target.value, index + 1, "pumpName")
                                            }
                                            value={pumpRow.pumpName}
                                        >
                                            {productOptions}
                                        </Select>
                                        {this.state.formData.pumpInformation[index + 1].pumpName === "Other" && (
                                            <TextField
                                                name="pumpActualName"
                                                label={translate(`Pump ${index + 2} Name`)}
                                                value={pumpRow.pumpActualName}
                                                onChange={e => this.updateFreeTextPumpRow(e, index + 1)}
                                            />
                                        )}
                                        <TextField
                                            name="pumpSerialNumber"
                                            label={translate(`Pump ${index + 2} Serial Number`)}
                                            value={pumpRow.pumpSerialNumber}
                                            onChange={e => this.updateFreeTextPumpRow(e, index + 1)}
                                        />
                                    </StyledWrapper>
                                </GridItem>

                                <GridItem {...styles.fourLinerGridItem}>
                                    <RadioGroup
                                        name={`pumpSerialReturnType${index + 1}`}
                                        label={translate(`Pump ${index + 2} Return Type`)}
                                        value={pumpRow.pumpReturnType}
                                        onChange={(e: React.FormEvent<HTMLFieldSetElement>) => {
                                            const target = e.currentTarget as HTMLFieldSetElement;
                                            const selectedValue = (target.querySelector("input:checked") as HTMLInputElement)?.value;
                                            if (selectedValue) {
                                                this.updatePumpRowReturnType(selectedValue, index + 1);
                                            }
                                        }}
                                    >
                                        <StyledWrapper {...styles.inlineCheckbox}>
                                            {pumpReturnTypes.map(pumpReturnType => (
                                                <Radio
                                                    value={pumpReturnType.value}
                                                    key={pumpReturnType.key}
                                                >
                                                    {translate(pumpReturnType.value)}
                                                </Radio>
                                            ))}
                                        </StyledWrapper>
                                    </RadioGroup>
                                </GridItem>

                                <GridItem {...styles.fourLinerGridItem}>
                                    <StyledWrapper {...styles.columnView}>
                                        <Select
                                            label={translate(`Pump ${index + 2} - Need a Replacement Rental?`)}
                                            onChange={e =>
                                                this.updatePumpRowField(e.target.value, index + 1, "needReplacementRental")
                                            }
                                            value={pumpRow.needReplacementRental}
                                        >
                                            {booleanDropdownOptions}
                                        </Select>
                                        <StyledWrapper {...styles.verticalSpacing}>
                                            <Select
                                                label={translate(`Pump ${index + 2} - Do you rent the current pump from Preferred Medical?`)}
                                                onChange={e => this.updatePumpRowField(e.target.value, index + 1, "rentCurrentPump")}
                                                value={pumpRow.rentCurrentPump}
                                            >
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>
                                            </Select>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>

                                <GridItem {...styles.fourLinerGridItem}>
                                    <TextArea
                                        name="pumpComments"
                                        label={translate(`Pump ${index + 2} Comments`)}
                                        value={pumpRow.pumpComments}
                                        onChange={e => this.updateFreeTextPumpRow(e, index + 1)}
                                    />
                                </GridItem>
                            </GridContainer>
                        ))}

                        <StyledWrapper {...styles.addButtonWrapper}>
                            <Button
                                {...styles.addButton}
                                onClick={() => this.addRow()}
                                data-test-selector="pumpReturnAddRow"
                                disabled={this.state.formData.pumpInformation.length >= maxNumberOfReturnPumps}
                            >
                                {translate("+ Add Another Row")}
                            </Button>
                        </StyledWrapper>

                        <GridItem {...styles.oneLinerGridItem}>
                            <StyledWrapper {...styles.submitButtonWrapper}>
                                <Button
                                    {...styles.submitButton}
                                    onClick={() => this.submitButtonClickHandler()}
                                    data-test-selector="pumpReturnSubmit"
                                    disabled={this.state.inProgress}
                                >
                                    {translate("Submit")}
                                </Button>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </>
        );
    }
}

export default PumpReturnForm;
